import dayjs from "dayjs";
import {
  CustomField,
  CustomFieldType,
  getCustomFieldIndex,
  Perm,
  Activity,
  Vent,
} from "../types";
import { apiSlice } from "./api";

/*
When using JSON.stringify, any JavaScript Date objects are automatically converted to
strings in the ISO 8601 format (e.g., "2023-10-26T18:30:00Z") before being serialized into the JSON string
*/

export interface GetVentRequest {
  id: string;
}
export interface GetVentResponse extends Vent {
  canUserEdit: boolean;
  canUserViewConfig: boolean;
  canUserViewGuests: boolean;
  userHostPerm: Perm | undefined; // Perm of the user for this event, if they are a host
  hostRecords: Record<
    string, // this is the email
    { name: string; email: string; perm: Perm; emailOptOut: boolean }
  >; // key is user ID
}

export interface GetVentDisplayInfoRequest {
  id: string;
  customFieldId?: string;
}
export interface GetVentDisplayInfoResponse {
  canUserEdit: boolean;
  canUserViewConfig: boolean;
  canUserViewGuests: boolean;
  activitiesExist: boolean;
  customFieldType: CustomFieldType;
  isUserGuestForVent: boolean;
}

export interface GetVentHomeRequest {
  id: string;
}
export interface GetVentHomeResponse {
  id: string;
  name: string;
  starts: string;
  ends?: string;
  descriptionHtml?: string;
  rsvpStart: string; // ISO 8601
  rsvpEnd: string; // ISO 8601
  allowPublicRsvps: boolean;
  allowGuestsView: boolean;
  excludeUndecidedGuests: boolean;
  excludeDeclinedGuests: boolean;
  location: string | undefined;
  locationUrl: string | undefined;
  plusOneMax: number;
  hasActivities: boolean;
  hasCustomFields: boolean; // provided so we can let the guest know there are custom fields to complete
  updateTime: string;
}

export interface GetVentsResponseRecord {
  id: string;
  name: string;
  starts: string;
  ends: string;
  canUserDelete: boolean;
  canUserClone: boolean;
  updateTime: string;
}
export interface GetVentsResponse {
  vents: GetVentsResponseRecord[];
}

export interface GetVentsRequest {
  includeEnded: boolean;
}

interface GetVentsForUserAsGuestResponseRecord {
  id: string;
  name: string;
  starts: string;
  ends: string;
  userGuestId: string; // guestId for user at this event
  canUserViewGuests: boolean;
  ventHasActivities: boolean;
  updateTime: string;
}
export interface GetVentForUserAsGuestsResponse {
  vents: GetVentsForUserAsGuestResponseRecord[];
}

export interface GetVentsForUserAsGuestsRequest {}

export interface CreateVentRequest {
  name: string;
  timeZone: string; // e.g. "America/New_York"
  starts: string;
}
export interface CreateVentResponse {
  id: string;
  name: string;
  starts: string; // ISO 8601
  ends: string; // ISO 8601
  updateTime: string; // ISO 8601
}

export interface CloneVentRequest {
  ventId: string;
  name: string;
  starts: string;
  cloneGuests: boolean;
  cloneActivities: boolean;
}

export interface CloneVentResponse {
  id: string;
  name: string;
  starts: string; // ISO 8601
  ends: string; // ISO 8601
  updateTime: string; // ISO 8601
}

export interface SaveVentResponse {
  id: string;
  name: string;
  starts: string; // ISO 8601
  ends: string; // ISO 8601
  // Server can change the URL, by adding https://
  locationUrl: string;
  updateTime: string; // ISO 8601
}

export interface SaveVentRequest {
  id: string;
  name: string;
  starts: string; // ISO 8601
  ends: string; // ISO 8601
  description: string | undefined;
  descriptionHtml: string | undefined;
  secondaryMax: number; // max number of plus ones per primary guest
  rsvpStart: string; // ISO 8601
  rsvpEnd: string; // ISO 8601
  rsvpStartDefault: boolean;
  rsvpEndDefault: boolean;
  allowPublicRsvps: boolean;
  allowGuestsView: boolean;
  excludeUndecidedGuests: boolean; // exclude guests with status "undecided"
  excludeDeclinedGuests: boolean; // exclude guests with status "declined"
  location: string | undefined;
  locationUrl: string | undefined;
  timeZone: string; // e.g. "America/New_York"
  allowGuestMessaging: boolean; // allow guests to message each other
  acceptedGuestLimit: number;
  updateTime: string;
}

export interface DeleteVentResponse {}
export interface DeleteVentRequest {
  ventId: string;
}

export interface CreateActivityRequest {
  activity: Omit<Activity, "id">;
  ventId: string;
  updateTime: string; // ISO 8601
}
export interface CreateActivityResponse {
  id: string;
  updateTime: string; // ISO 8601
}

export interface SaveActivityRequest {
  activityId: string;
  activity: Activity;
  ventId: string;
  updateTime: string; // ISO 8601
}
export interface SaveActivityResponse {
  id: string;
  updateTime: string; // ISO 8601
}

export interface DeleteActivityRequest {
  activityId: string;
  ventId: string; // Needed to easily update cache
  updateTime: string; // ISO 8601 update time of vent
}
export interface DeleteActivityResponse {
  updateTime: string; // ISO 8601 update time of vent
}

export interface SaveHostRequest {
  ventId: string;
  hostEmail: string;
  perm: Perm;
  emailOptOut: boolean;
  updateTime: string; // ISO 8601
}
export interface SaveHostResponse {
  updateTime: string; // ISO 8601
}

export interface CreateHostRequest {
  ventId: string;
  hostEmail: string;
  perm: Perm;
  updateTime: string; // ISO 8601
}
export interface CreateHostResponse {
  userId: string;
  name: string;
  updateTime: string; // ISO 8601
}

export interface DeleteHostRequest {
  hostEmail: string;
  ventId: string;
  updateTime: string;
}
export interface DeleteHostResponse {
  updateTime: string; // ISO 8601 update time of vent
}

export interface CreateCustomFieldRequest {
  ventId: string;
  name: string;
  fieldType: CustomFieldType;
  updateTime: string; // ISO 8601
}
export interface CreateCustomFieldResponse {
  id: string; // ID (GUID) of the custom field
  updateTime: string; // ISO 8601
}

export interface SaveCustomFieldRequest extends Omit<CustomField, "fieldType"> {
  ventId: string;
  updateTime: string; // ISO 8601
}
export interface SaveCustomFieldResponse {
  updateTime: string; // ISO 8601
}

export interface MoveCustomFieldRequest {
  id: string;
  newIndex: number;
  ventId: string;
  updateTime: string; // ISO 8601
}
export interface MoveCustomFieldResponse {
  updateTime: string; // ISO 8601
}

export interface DeleteCustomFieldRequest {
  customFieldId: string;
  ventId: string; // Needed to easily update cache
  updateTime: string; // ISO 8601 update time of vent
}
export interface DeleteCustomFieldResponse {
  updateTime: string; // ISO 8601 update time of vent
}

export const ventsApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // getVent gets a single event
    getVent: builder.query<GetVentResponse, GetVentRequest>({
      query: (body) => ({
        url: "getVent",
        body: JSON.stringify(body),
      }),
    }),

    getVentDisplayInfo: builder.query<
      GetVentDisplayInfoResponse,
      GetVentDisplayInfoRequest
    >({
      query: (body) => ({
        url: "getVentDisplayInfo",
        body: JSON.stringify(body),
      }),
      providesTags: ["Connect"], // used to bust cache when user connects to own guest record
    }),

    // getVent gets a single event for the home page (unauthenticated)
    getVentHome: builder.query<GetVentHomeResponse, GetVentHomeRequest>({
      query: (body) => ({
        url: "getVentHome",
        body: JSON.stringify(body),
      }),
    }),
    // getVents gets all events that a user is hosting
    getVents: builder.query<GetVentsResponse, GetVentsRequest>({
      query: (body) => ({
        url: "getVents",
        body: JSON.stringify(body),
      }),
    }),

    // getVentsForUserAsGuest gets all events for which a user is a guest
    getVentsForUserAsGuest: builder.query<
      GetVentForUserAsGuestsResponse,
      GetVentsForUserAsGuestsRequest
    >({
      query: (body) => ({
        url: "getVentsForUserAsGuest",
        body: JSON.stringify(body),
      }),
      providesTags: ["Connect"], // used to bust cache when user connects to own guest record
    }),

    // createVent creates a minimal event
    createVent: builder.mutation<CreateVentResponse, CreateVentRequest>({
      query: (body) => ({
        url: "createVent",
        body: JSON.stringify(body),
      }),
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        const undos: any[] = []; // PatchCollection is not exposed
        try {
          const { data } = await queryFulfilled;
          undos.push(
            dispatch(
              ventsApi.util.updateQueryData(
                "getVents",
                { includeEnded: dayjs().isAfter(dayjs(data.ends)) },
                (draft) => {
                  draft.vents.push({
                    id: data.id,
                    name: request.name,
                    starts: request.starts,
                    ends: data.ends,
                    canUserClone: true,
                    canUserDelete: true,
                    updateTime: data.updateTime,
                  });
                }
              )
            )
          );
        } catch {
          for (const u of undos) {
            (u as any).undo();
          }
        }
      },
    }),

    cloneVent: builder.mutation<CloneVentResponse, CloneVentRequest>({
      query: (body) => ({
        url: "cloneVent",
        body: JSON.stringify(body),
      }),
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        const undos: any[] = []; // PatchCollection is not exposed
        try {
          const { data } = await queryFulfilled;
          undos.push(
            dispatch(
              ventsApi.util.updateQueryData(
                "getVents",
                { includeEnded: dayjs().isAfter(dayjs(data.ends)) },
                (draft) => {
                  draft.vents.push({
                    id: data.id,
                    name: request.name,
                    starts: request.starts,
                    ends: data.ends,
                    canUserClone: true,
                    canUserDelete: true,
                    updateTime: data.updateTime,
                  });
                }
              )
            )
          );
        } catch {
          for (const u of undos) {
            (u as any).undo();
          }
        }
      },
    }),

    createActivity: builder.mutation<
      CreateActivityResponse,
      CreateActivityRequest
    >({
      query: (body) => ({
        url: "createActivity",
        body: JSON.stringify(body),
      }),
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        const undos: any[] = []; // PatchCollection is not exposed
        try {
          const { data } = await queryFulfilled;
          undos.push(
            dispatch(
              ventsApi.util.updateQueryData(
                "getVent",
                { id: request.ventId },
                (draft) => {
                  if (draft.activities == null) {
                    draft.activities = {};
                  }
                  draft.activities[data.id] = {
                    description: request.activity.description,
                    limitPerEvent: request.activity.limitPerEvent,
                    limitPerGuest: request.activity.limitPerGuest,
                    hostManaged: request.activity.hostManaged,
                  };
                  draft.updateTime = data.updateTime;
                }
              )
            )
          );
        } catch {
          for (const u of undos) {
            (u as any).undo();
          }
        }
      },
    }),

    saveActivity: builder.mutation<SaveActivityResponse, SaveActivityRequest>({
      query: (body) => ({
        url: "saveActivity",
        body: JSON.stringify(body),
      }),
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        const undos: any[] = []; // PatchCollection is not exposed
        try {
          const { data } = await queryFulfilled;
          undos.push(
            dispatch(
              ventsApi.util.updateQueryData(
                "getVent",
                { id: request.ventId },
                (draft) => {
                  draft.activities[request.activityId] = {
                    description: request.activity.description,
                    limitPerEvent: request.activity.limitPerEvent,
                    limitPerGuest: request.activity.limitPerGuest,
                    hostManaged: request.activity.hostManaged,
                  };
                  draft.updateTime = data.updateTime;
                }
              )
            )
          );
        } catch {
          for (const u of undos) {
            (u as any).undo();
          }
        }
      },
    }),

    deleteActivity: builder.mutation<
      DeleteActivityResponse,
      DeleteActivityRequest
    >({
      query: (body) => ({
        url: "deleteActivity",
        body: JSON.stringify(body),
      }),
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        const undos: any[] = []; // PatchCollection is not exposed
        try {
          const { data } = await queryFulfilled;
          undos.push(
            dispatch(
              ventsApi.util.updateQueryData(
                "getVent",
                { id: request.ventId },
                (draft) => {
                  delete draft.activities[request.activityId];
                  draft.updateTime = data.updateTime;
                }
              )
            )
          );
        } catch {
          for (const u of undos) {
            (u as any).undo();
          }
        }
      },
    }),

    saveVent: builder.mutation<SaveVentResponse, SaveVentRequest>({
      query: (body) => ({
        url: "saveVent",
        body: JSON.stringify(body),
      }),
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        const undos: any[] = []; // PatchCollection is not exposed
        try {
          const { data } = await queryFulfilled;
          undos.push(
            dispatch(
              ventsApi.util.updateQueryData(
                "getVents",
                { includeEnded: dayjs().isAfter(dayjs(data.ends)) },
                (draft) => {
                  const updatedVents = draft.vents.map((record) => {
                    if (record.id === data.id) {
                      return {
                        id: data.id,
                        name: request.name,
                        starts: request.starts,
                        ends: request.ends,
                        canUserClone: record.canUserClone,
                        canUserDelete: record.canUserDelete,
                        updateTime: data.updateTime,
                      };
                    } else return record;
                  });
                  draft.vents = updatedVents;
                }
              )
            )
          );

          undos.push(
            dispatch(
              ventsApi.util.updateQueryData(
                "getVentHome",
                { id: data.id },
                (draft) => {
                  draft.name = request.name;
                  draft.starts = request.starts;
                  draft.ends = request.ends;
                  draft.descriptionHtml = request.descriptionHtml;
                  draft.allowGuestsView = request.allowGuestsView;
                  draft.excludeDeclinedGuests = request.excludeDeclinedGuests;
                  draft.excludeUndecidedGuests = request.excludeUndecidedGuests;
                  draft.allowPublicRsvps = request.allowPublicRsvps;
                  draft.rsvpStart = request.rsvpStart;
                  draft.rsvpEnd = request.rsvpEnd;
                  draft.location = request.location;
                  draft.locationUrl = data.locationUrl;
                  draft.plusOneMax = request.secondaryMax;
                  draft.updateTime = data.updateTime;
                }
              )
            )
          );

          undos.push(
            dispatch(
              ventsApi.util.updateQueryData(
                "getVent",
                { id: data.id },
                (draft) => {
                  draft.name = request.name;
                  draft.starts = request.starts;
                  draft.ends = request.ends;
                  draft.description = request.description;
                  draft.descriptionHtml = request.descriptionHtml;
                  draft.secondaryMax = request.secondaryMax;
                  draft.allowGuestsView = request.allowGuestsView;
                  draft.excludeUndecidedGuests = request.excludeUndecidedGuests;
                  draft.excludeDeclinedGuests = request.excludeDeclinedGuests;
                  draft.allowPublicRsvps = request.allowPublicRsvps;
                  draft.rsvpStart = request.rsvpStart;
                  draft.rsvpEnd = request.rsvpEnd;
                  draft.rsvpStartDefault = request.rsvpStartDefault;
                  draft.rsvpEndDefault = request.rsvpEndDefault;
                  draft.location = request.location;
                  draft.locationUrl = data.locationUrl;
                  draft.timeZone = request.timeZone;
                  draft.allowGuestMessaging = request.allowGuestMessaging;
                  draft.acceptedGuestLimit = request.acceptedGuestLimit;
                  draft.updateTime = data.updateTime;
                }
              )
            )
          );
          //
        } catch {
          for (const u of undos) {
            (u as any).undo();
          }
        }
      },
    }),

    saveHost: builder.mutation<SaveHostResponse, SaveHostRequest>({
      query: (body) => {
        return {
          url: "saveHost",
          body: JSON.stringify(body),
        };
      },
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            ventsApi.util.updateQueryData(
              "getVent",
              { id: request.ventId },
              (draft) => {
                draft.hostRecords[request.hostEmail].perm = request.perm;
                draft.hostRecords[request.hostEmail].emailOptOut =
                  request.emailOptOut;
                draft.updateTime = data.updateTime;
              }
            )
          );
        } catch {}
      },
    }),

    createHost: builder.mutation<CreateHostResponse, CreateHostRequest>({
      query: (body) => {
        return {
          url: "createHost",
          body: JSON.stringify(body),
        };
      },
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            ventsApi.util.updateQueryData(
              "getVent",
              { id: request.ventId },
              (draft) => {
                draft.hostRecords[request.hostEmail] = {
                  perm: request.perm,
                  email: request.hostEmail,
                  emailOptOut: false,
                  name: data.name,
                };
                draft.updateTime = data.updateTime;
              }
            )
          );
        } catch {}
      },
    }),

    deleteHost: builder.mutation<DeleteHostResponse, DeleteHostRequest>({
      query: (body) => ({
        url: "deleteHost",
        body: JSON.stringify(body),
      }),
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        const undos: any[] = []; // PatchCollection is not exposed
        try {
          const { data } = await queryFulfilled;
          undos.push(
            dispatch(
              ventsApi.util.updateQueryData(
                "getVent",
                { id: request.ventId },
                (draft) => {
                  delete draft.hostRecords[request.hostEmail];
                  draft.updateTime = data.updateTime;
                }
              )
            )
          );
        } catch {
          for (const u of undos) {
            (u as any).undo();
          }
        }
      },
    }),

    deleteVent: builder.mutation<DeleteVentResponse, DeleteVentRequest>({
      query: (body) => ({
        url: "deleteVent",
        body: JSON.stringify(body),
      }),
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        const undos: any[] = []; // PatchCollection is not exposed
        try {
          await queryFulfilled;
          undos.push(
            dispatch(
              ventsApi.util.updateQueryData(
                "getVents",
                { includeEnded: true },
                (draft) => {
                  for (let i = 0; i < draft.vents.length; i++) {
                    if (request.ventId === draft.vents[i].id) {
                      draft.vents.splice(i, 1);
                      return;
                    }
                  }
                }
              )
            )
          );
          undos.push(
            dispatch(
              ventsApi.util.updateQueryData(
                "getVents",
                { includeEnded: false },
                (draft) => {
                  for (let i = 0; i < draft.vents.length; i++) {
                    if (request.ventId === draft.vents[i].id) {
                      draft.vents.splice(i, 1);
                      return;
                    }
                  }
                }
              )
            )
          );

          undos.push(
            dispatch(
              ventsApi.util.updateQueryData(
                "getVent",
                { id: request.ventId },
                () => {
                  return undefined;
                }
              )
            )
          );
        } catch (e) {
          console.log("Undo: ", e);
          for (const u of undos) {
            (u as any).undo();
          }
        }
      },
    }),

    createCustomField: builder.mutation<
      CreateCustomFieldResponse,
      CreateCustomFieldRequest
    >({
      query: (body) => ({
        url: "createCustomField",
        body: JSON.stringify(body),
      }),
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        const undos: any[] = []; // PatchCollection is not exposed
        try {
          const { data } = await queryFulfilled;
          undos.push(
            dispatch(
              ventsApi.util.updateQueryData(
                "getVent",
                { id: request.ventId },
                (draft) => {
                  draft.updateTime = data.updateTime; // vent update time
                  if (draft.customFields == null) {
                    draft.customFields = [];
                  }
                  switch (request.fieldType) {
                    case "t": {
                      draft.customFields.push({
                        id: data.id,
                        fieldType: request.fieldType,
                        hidden: false,
                        name: request.name,
                        description: "",
                        configText: { required: false },
                      });
                      break;
                    }
                    case "o": {
                      draft.customFields.push({
                        id: data.id,
                        fieldType: request.fieldType,
                        hidden: false,
                        name: request.name,
                        description: "",
                        configOptions: {
                          minOptions: 0, // we are cheating here -- we know what the server is doing
                          maxOptions: 0,
                          options: ["Yes", "No"],
                        },
                      });
                      break;
                    }
                  }
                }
              )
            )
          );
        } catch {
          for (const u of undos) {
            (u as any).undo();
          }
        }
      },
    }),

    saveCustomField: builder.mutation<
      SaveCustomFieldResponse,
      SaveCustomFieldRequest
    >({
      query: (body) => ({
        url: "saveCustomField",
        body: JSON.stringify(body),
      }),
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        const undos: any[] = []; // PatchCollection is not exposed
        try {
          const { data } = await queryFulfilled;
          undos.push(
            dispatch(
              ventsApi.util.updateQueryData(
                "getVent",
                { id: request.ventId },
                (draft) => {
                  const customFieldIndex = getCustomFieldIndex(
                    draft.customFields,
                    request.id
                  );
                  if (customFieldIndex < 0) {
                    throw new Error("Custom field not found");
                  }
                  draft.customFields[customFieldIndex].name = request.name;
                  draft.customFields[customFieldIndex].description =
                    request.description;
                  draft.customFields[customFieldIndex].hidden = request.hidden;
                  draft.customFields[customFieldIndex].configText =
                    request.configText;
                  draft.customFields[customFieldIndex].configOptions =
                    request.configOptions;
                  draft.updateTime = data.updateTime;
                }
              )
            )
          );
        } catch {
          for (const u of undos) {
            (u as any).undo();
          }
        }
      },
    }),

    moveCustomField: builder.mutation<
      MoveCustomFieldResponse,
      MoveCustomFieldRequest
    >({
      query: (body) => ({
        url: "moveCustomField",
        body: JSON.stringify(body),
      }),
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        const undos: any[] = []; // PatchCollection is not exposed
        try {
          const { data } = await queryFulfilled;
          undos.push(
            dispatch(
              ventsApi.util.updateQueryData(
                "getVent",
                { id: request.ventId },
                (draft) => {
                  draft.updateTime = data.updateTime;
                  const customFieldIndex = getCustomFieldIndex(
                    draft.customFields,
                    request.id
                  );
                  if (customFieldIndex < 0) {
                    throw new Error("Custom field not found");
                  }
                  if (request.newIndex == customFieldIndex) return; // nothing to do
                  const newCustomFields: CustomField[] = [];
                  draft.customFields.forEach((cf, i) => {
                    if (i === customFieldIndex) return;
                    if (i > customFieldIndex) {
                      newCustomFields.push(cf);
                    }
                    if (i === request.newIndex) {
                      newCustomFields.push(
                        draft.customFields[customFieldIndex]
                      );
                    }
                    if (i < customFieldIndex) {
                      newCustomFields.push(cf);
                    }
                  });
                  draft.customFields = newCustomFields;
                }
              )
            )
          );
        } catch {
          for (const u of undos) {
            (u as any).undo();
          }
        }
      },
    }),

    deleteCustomField: builder.mutation<
      DeleteCustomFieldResponse,
      DeleteCustomFieldRequest
    >({
      query: (body) => ({
        url: "deleteCustomField",
        body: JSON.stringify(body),
      }),
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        const undos: any[] = []; // PatchCollection is not exposed
        try {
          const { data } = await queryFulfilled;
          undos.push(
            dispatch(
              ventsApi.util.updateQueryData(
                "getVent",
                { id: request.ventId },
                (draft) => {
                  const customFieldIndex = getCustomFieldIndex(
                    draft.customFields,
                    request.customFieldId
                  );
                  draft.customFields.splice(customFieldIndex, 1);
                  draft.updateTime = data.updateTime;
                }
              )
            )
          );
        } catch {
          for (const u of undos) {
            (u as any).undo();
          }
        }
      },
    }),

    //
  }),

  //
});

export const {
  useGetVentQuery,
  useCreateVentMutation,
  useGetVentsQuery,
  useGetVentsForUserAsGuestQuery,
  useSaveVentMutation,
  useDeleteVentMutation,
  useCreateActivityMutation,
  useSaveActivityMutation,
  useDeleteActivityMutation,
  useGetVentHomeQuery,
  useSaveHostMutation,
  useCreateHostMutation,
  useDeleteHostMutation,
  useCloneVentMutation,
  useGetVentDisplayInfoQuery,
  useCreateCustomFieldMutation,
  useSaveCustomFieldMutation,
  useDeleteCustomFieldMutation,
  useMoveCustomFieldMutation,
} = ventsApi;
